import React, {CSSProperties, ReactNode, createContext, useContext, useEffect, useRef} from 'react'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import type {UniqueIdentifier} from '@dnd-kit/core'

import t from '../../../helpers/Languages.js'
import IconButton from '../buttons/IconButton.js'

export interface SortableItemProps {
    id: UniqueIdentifier
    disabled?: boolean
    /** Only a DragHandle in the children starts a drag, instead of the whole item */
    dragHandle?: boolean
    children?: ReactNode
}

type SortableState = ReturnType<typeof useSortable>

// What a DragHandle in the item needs to start the drag
const DragHandleContext = createContext<Pick<SortableState, 'attributes' | 'listeners' | 'setActivatorNodeRef'> | null>(null)

/**
 * The button that drags its item, in a SortableList with dragHandle (renderItem gets it as second argument). It is a
 * real button, so the item can also be moved with the keyboard: Space or Enter picks it up, the arrow keys move it.
 */
export function DragHandle() {
    const handle = useContext(DragHandleContext)

    return (
        <IconButton
            aria-label={t('drag')}
            {...handle?.attributes}
            {...handle?.listeners}
            ref={handle?.setActivatorNodeRef}
            icon='mdi mdi-drag-horizontal-variant'
            onClick={(event) => event.preventDefault()}
        />
    )
}

/** An item of SortableList: dragged as a whole, or by the DragHandle in it when dragHandle */
export default function SortableItem({id, disabled, dragHandle, children}: SortableItemProps) {
    const {
        isDragging,
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition
    } = useSortable({
        id,
        disabled,
        animateLayoutChanges: () => false
    })

    const isDraggingRef = useRef(false)
    const elementRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        isDraggingRef.current = isDragging
    }, [isDragging])

    // Stops the page from scrolling while an item is dragged by touch. Added on the element itself, because React's
    // touchmove listeners are passive and can't prevent the default.
    useEffect(() => {
        const el = elementRef.current

        if (!el) {
            return
        }

        const onTouchMove = (event: TouchEvent) => {
            if (isDraggingRef.current) {
                event.preventDefault()
            }
        }

        el.addEventListener('touchmove', onTouchMove, {passive: false})

        return () => el.removeEventListener('touchmove', onTouchMove)
    }, [])

    const style: CSSProperties = {
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        // Translate only: the scale dnd-kit adds would stretch an item dragged over an item with a different height
        transform: CSS.Translate.toString(transform),
        transition,
        touchAction: isDragging ? 'none' : 'auto'
    }

    return (
        <div
            ref={(node) => {
                elementRef.current = node
                setNodeRef(node)
            }}
            id={`sortable${id}`}
            style={style}
            {...(dragHandle ? {} : {...attributes, ...listeners})}
        >
            {dragHandle ?
                <DragHandleContext.Provider value={{attributes, listeners, setActivatorNodeRef}}>
                    {children}
                </DragHandleContext.Provider> :
                children
            }
        </div>
    )
}
